import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, addYears } from 'date-fns';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { RecurringContractDA } from '../../services/admin/recurring.contract.service';
import { ApiDigitalAdminContact } from '../../services/admin/contact.service';
import { TreasuryAccountDA } from '../../services/admin/treasury.account.service';
import { normalizeContacts } from '../../scripts/format.value.script';
import { Asset } from '../../models/asset.model';
import { Service } from '../../models/service.model';
import { AppService } from '../../models/app.service.model';
import { Contact } from '../../models/contact.model';
import { TreasuryAccount } from '../../models/treasury.account.model';
import { Error } from '../../models/error.model';
import { GlobalSwal } from '../../styles/swal.style';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { modalHide } from '../../utils/modal.util';
import { legendValid, legendInvalid } from '../../utils/legend.data.util';

import AppLegend from '../element/Legend';
import AppInputContact from '../element/InputContact';
import AppInputTreasuryAccount from '../element/InputTreasuryAccount';
import AppInputQrType from '../element/InputQrType';

export interface ModalAssetRentProps {
  appServiceForUser: AppService | null;
  companyForUser: any;
  asset: Asset | null;
  servicesForCompany: Service[] | null;
}

let errorResponse: Error;

const CADENCE_LABELS: Record<string, string> = {
  day: 'cadenceDay',
  week: 'cadenceWeek',
  month: 'cadenceMonth',
  quarter: 'cadenceQuarter',
  year: 'cadenceYear',
}

const AppModalAssetRent: React.FunctionComponent<ModalAssetRentProps> = ({appServiceForUser, companyForUser, asset, servicesForCompany}) => {
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [contacts, setContacts] = useState<Contact[]>([])
  const [treasuryAccounts, setTreasuryAccounts] = useState<TreasuryAccount[]>([])
  const [contact, setContact] = useState<{value: string, valid: boolean, object: any}>({value: '', valid: false, object: null})
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(-1)
  const [cadenceUnit, setCadenceUnit] = useState('month')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [autoRenew, setAutoRenew] = useState(false)
  const [renewDaysBefore, setRenewDaysBefore] = useState('30')
  const [depositAmount, setDepositAmount] = useState('0')
  const [priceUnit, setPriceUnit] = useState('0')
  const [taxRate, setTaxRate] = useState(0)
  const [taxCode, setTaxCode] = useState('')
  const [treasuryAccount, setTreasuryAccount] = useState<{value: string, valid: boolean, object: any}>({value: '', valid: false, object: null})
  const [qrType, setQrType] = useState<{value: string, valid: boolean}>({value: 'none', valid: true})
  const [autoBilling, setAutoBilling] = useState(false)

  const rentalOptions = asset?.rental_options ?? []

  const optionService = (index: number): Service | null => {
    const opt = rentalOptions[index]
    if (!opt) return null
    const sid = typeof opt.id_service === 'string' ? opt.id_service : (opt.id_service as any)?.toString()
    return servicesForCompany?.find(s => s.id === sid) ?? null
  }

  const cadenceLabel = (unit: string): string => {
    const key = CADENCE_LABELS[unit]
    return key ? ((lang.labels as any)[key] ?? unit) : unit
  }

  const selectOption = (index: number) => {
    setSelectedOptionIndex(index)
    const opt = rentalOptions[index]
    const svc = optionService(index)
    if (opt) setCadenceUnit(opt.cadence_unit)
    if (svc) {
      setPriceUnit(String(svc.price_sale?.amount ?? 0))
      setTaxRate(svc.price_sale?.tax_rate ?? 0)
      setTaxCode(svc.price_sale?.tax_code ?? '')
    } else {
      setPriceUnit('0')
      setTaxRate(0)
      setTaxCode('')
    }
  }

  const loadContacts = useCallback(async (id_company: string) => {
    try {
      const response = await ApiDigitalAdminContact.getContacts(id_company, '', '', '', '', 1, 1000)
      if (response.status === 200) {
        const payload = response.data ?? {}
        if (Array.isArray(payload.items)) {
          setContacts(normalizeContacts(payload.items))
        } else if (Array.isArray(payload)) {
          setContacts(normalizeContacts(payload))
        } else if (Array.isArray(payload.contacts)) {
          setContacts(normalizeContacts(payload.contacts))
        } else {
          setContacts([])
        }
      }
    } catch (_) { /* silent */ }
  }, [])

  const loadTreasuryAccounts = async (id_company: string) => {
    try {
      const response = await TreasuryAccountDA.getTreasuryAccounts(id_company)
      if (response.status === 200) {
        const data = response.data
        setTreasuryAccounts(data?.items ?? data?.treasury_accounts ?? (Array.isArray(data) ? data : []))
      }
    } catch (_) { /* silent */ }
  }

  const resetForm = () => {
    setContact({value: '', valid: false, object: null})
    setSelectedOptionIndex(-1)
    setCadenceUnit('month')
    const today = new Date()
    setStartDate(format(today, 'yyyy-MM-dd'))
    setEndDate(format(addYears(today, 1), 'yyyy-MM-dd'))
    setAutoRenew(false)
    setRenewDaysBefore('30')
    setDepositAmount(String(asset?.default_deposit_amount ?? 0))
    setPriceUnit('0')
    setTaxRate(0)
    setTaxCode('')
    setQrType({value: 'none', valid: true})
    setAutoBilling(false)
    // Re-select default treasury account
    if (treasuryAccounts.length > 0) {
      const def = treasuryAccounts.find(t => t.default) ?? treasuryAccounts[0]
      if (def) setTreasuryAccount({value: def.id, valid: true, object: def})
    } else {
      setTreasuryAccount({value: '', valid: false, object: null})
    }
  }

  const validateForm = (): boolean => {
    let valid = true

    if (!contact.valid) {
      legendInvalid('legend-contact-required')
      valid = false
    } else {
      legendValid('legend-contact-required')
    }

    if (!treasuryAccount.valid) {
      legendInvalid('legend-treasury-account-required')
      valid = false
    } else {
      legendValid('legend-treasury-account-required')
    }

    if (selectedOptionIndex < 0) {
      legendInvalid('legend-rental-option-required')
      valid = false
    } else {
      legendValid('legend-rental-option-required')
    }

    if (!startDate) {
      legendInvalid('legend-rental-start-required')
      valid = false
    } else {
      legendValid('legend-rental-start-required')
    }

    const parsedPrice = parseFloat(priceUnit)
    if (isNaN(parsedPrice) || parsedPrice < 0) {
      legendInvalid('legend-rental-price-valid')
      valid = false
    } else {
      legendValid('legend-rental-price-valid')
    }

    const parsedDeposit = parseFloat(depositAmount)
    if (depositAmount && (isNaN(parsedDeposit) || parsedDeposit < 0)) {
      legendInvalid('legend-rental-deposit-valid')
      valid = false
    } else {
      legendValid('legend-rental-deposit-valid')
    }

    if (autoBilling && selectedOptionIndex >= 0) {
      const svc = optionService(selectedOptionIndex)
      const hasAccount = !!(svc?.product_account_id || svc?.accounts?.income)
      if (!hasAccount) {
        legendInvalid('legend-auto-billing-account-required')
        valid = false
      } else {
        legendValid('legend-auto-billing-account-required')
      }
    } else {
      legendValid('legend-auto-billing-account-required')
    }

    return valid
  }

  const submitRental = async () => {
    if (!companyForUser || !asset || !validateForm()) return

    setLoading(true)

    try {
      const service = optionService(selectedOptionIndex)
      const depositAmt = parseFloat(depositAmount) || 0
      const price = parseFloat(priceUnit) || 0

      const companyData = {
        uuid: companyForUser.uuid,
        name: companyForUser.name,
        logo: companyForUser.logo,
        address: companyForUser.address,
        phone: companyForUser.phone || '',
        email: companyForUser.email || '',
        website: companyForUser.website || '',
        vat_number: companyForUser.vat_number || ''
      }

      const serviceAccountId = service?.product_account_id ?? service?.accounts?.income ?? null

      const serviceContent: any = {
        detail: service?.display_name ?? asset.name,
        reference: service?.id ?? '',
        quantity: '1',
        price_unit: String(price),
        tax_included: false,
        tax_code: taxCode,
        tax_rate: taxRate,
        rebate_type: 'none',
        rebate_value: 0,
        rebate_quantity: 0,
        rebate_amount: '0',
        unit: service?.unit ? { id: service.unit.id, type: service.unit.type || 'service', name: service.unit.name, symbol: service.unit.symbol } : null,
        id_account: serviceAccountId,
      }

      const data: any = {
        label: `${lang.labels.rentAsset} — ${asset.name}`,
        id_contact: contact.value,
        id_asset: asset.id,
        template: {
          concerning: asset.description || '',
          currency: companyForUser.currency,
          treasury_account: treasuryAccount.object ?? undefined,
          requirements: [{
            type: 'service',
            description: service?.display_name ?? asset.name,
            contents: [serviceContent]
          }],
          qr_type: qrType.value,
          language: companyForUser.language ?? lang.code,
          payment_terms_days: 30,
          company: companyData,
        },
        cadence: {
          unit: cadenceUnit,
          interval: rentalOptions[selectedOptionIndex]?.cadence_interval ?? 1,
          anchor_day: 1,
        },
        start_date: startDate,
        end_date: endDate || null,
        issue_offset_days: 0,
        auto_validate: autoBilling,
        auto_send: autoBilling,
        auto_renew: autoRenew,
        renew_days_before: autoRenew ? Math.max(parseInt(renewDaysBefore) || 0, 0) : 0,
      }

      if (depositAmt > 0) {
        data.security_deposit = { amount: depositAmt }
      }

      const response = await RecurringContractDA.postRecurringContract(companyForUser.id, data)

      if (response.status === 200 || response.status === 201) {
        resetForm()
        modalHide('modal-asset-rent')
        const contractId = response.data?.id
        GlobalSwal.fire({
          title: lang.labels.rentalCreatedSuccess,
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        }).then(() => {
          if (contractId) {
            navigate('/main/sale/recurring-contract/view/' + contractId, {replace: true})
          } else {
            navigate('/main/sale/recurring-contract/list', {replace: true})
          }
        })
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (companyForUser) {
      loadContacts(companyForUser.id)
      loadTreasuryAccounts(companyForUser.id)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect(() => {
    if (treasuryAccounts.length > 0) {
      const def = treasuryAccounts.find(t => t.default) ?? treasuryAccounts[0]
      if (def) setTreasuryAccount({value: def.id, valid: true, object: def})
    }
  }, [treasuryAccounts])

  useEffect(() => {
    if (asset) {
      resetForm()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset])

  const hasOptions = rentalOptions.length > 0
  const optionSelected = selectedOptionIndex >= 0

  return (
    <div id="modal-asset-rent" className="modal fade" tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered mw-650px">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="fw-bolder">{lang.labels.rentThisAsset}</h2>
            <div className="btn btn-icon btn-sm btn-active-icon-primary" data-bs-dismiss="modal">
              <span className="svg-icon svg-icon-1">
                <i className="bi bi-x-lg"></i>
              </span>
            </div>
          </div>
          <div className="modal-body scroll-y mx-5 my-3">
            { asset &&
              <div className="d-flex justify-content-between align-items-center bg-light-primary rounded p-4 mb-5">
                <span className="fw-bold text-muted">{lang.labels.asset}</span>
                <span className="fw-bolder">{asset.name} {asset.code ? `(${asset.code})` : ''}</span>
              </div>
            }
            <div className="mb-5">
              <label className="form-label required">{lang.labels.tenant}</label>
              { appServiceForUser && companyForUser &&
                <AppInputContact appServiceForUser={appServiceForUser} companyForUser={companyForUser} contact={contact} setContact={setContact} setQrTtype={setQrType} loadContactsForCompany={loadContacts}></AppInputContact>
              }
            </div>
            <div className="mb-5">
              <label className="form-label required">{lang.labels.treasuryAccount}</label>
              <AppInputTreasuryAccount solid={true} treasuryAccountsForCompany={treasuryAccounts} treasuryAccount={treasuryAccount} setTreasuryAccount={setTreasuryAccount} />
            </div>
            <div className="mb-5">
              <label className="form-label">{lang.labels.qrType}</label>
              <AppInputQrType qrType={qrType} setQrType={setQrType} />
            </div>
            <div className="mb-5">
              <div className="d-flex flex-stack">
                <div className="d-flex flex-column">
                  <label className="form-label mb-0">{lang.labels.autoBilling}</label>
                </div>
                <div className="d-flex justify-content-end">
                  <div className="form-check form-check-solid form-check-custom form-switch">
                    <input className="form-check-input w-50px h-30px" type="checkbox" checked={autoBilling} onChange={(e) => setAutoBilling(e.target.checked)} />
                  </div>
                </div>
              </div>
              { autoBilling && selectedOptionIndex >= 0 && !(optionService(selectedOptionIndex)?.product_account_id || optionService(selectedOptionIndex)?.accounts?.income) &&
                <div className="text-danger fs-8 mt-2">{lang.labels.autoBillingRequiresAccount}</div>
              }
              <AppLegend attribute={{check: !(autoBilling && selectedOptionIndex >= 0 && !(optionService(selectedOptionIndex)?.product_account_id || optionService(selectedOptionIndex)?.accounts?.income)), name: "auto-billing-account", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}} />
            </div>
            { !hasOptions &&
              <div className="alert alert-warning py-3 mb-5">
                <span className="fw-bold">{lang.labels.noRentalOptionConfigured}</span>
              </div>
            }
            { hasOptions &&
              <div className="mb-5">
                <label className="form-label required">{lang.labels.rentalOption}</label>
                <select
                  className="form-select"
                  value={selectedOptionIndex}
                  onChange={e => selectOption(parseInt(e.target.value))}
                >
                  <option value={-1} disabled>{lang.labels.selectRentalOption}</option>
                  { rentalOptions.map((opt, i) => {
                    const svc = optionService(i)
                    const label = cadenceLabel(opt.cadence_unit)
                    const svcName = svc?.display_name ?? svc?.name ?? '?'
                    const price = svc?.price_sale?.amount != null ? `${svc.price_sale.amount.toFixed(2)} ${companyForUser?.currency?.code ?? 'CHF'}` : ''
                    return <option key={i} value={i}>{label} — {svcName}{price ? ` — ${price}` : ''}</option>
                  })}
                </select>
                <AppLegend attribute={{check: optionSelected, name: "rental-option", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}} />
              </div>
            }
            { optionSelected &&
              <>
                <div className="mb-5">
                  <div className="row">
                    <div className="col-8">
                      <label className="form-label">{lang.labels.unitPrice}</label>
                      <input
                        className="form-control"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={priceUnit}
                        onChange={(e) => setPriceUnit(e.target.value)}
                        style={{fontVariantNumeric: 'tabular-nums'}}
                      />
                      <AppLegend attribute={{check: !isNaN(parseFloat(priceUnit)) && parseFloat(priceUnit) >= 0, name: "rental-price", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}} />
                    </div>
                    <div className="col-4">
                      <label className="form-label">{lang.labels.taxRate}</label>
                      <div className="form-control bg-light" style={{fontVariantNumeric: 'tabular-nums'}}>{(taxRate * 100).toFixed(1)}%</div>
                    </div>
                  </div>
                </div>
                <div className="mb-5">
                  <label className="form-label">{lang.labels.rentalCadence}</label>
                  <div className="form-control bg-light">{cadenceLabel(cadenceUnit)}</div>
                </div>
              </>
            }
            <div className="row mb-5">
              <div className="col-6">
                <label className="form-label required">{lang.labels.rentalStart}</label>
                <input className="form-control" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                <AppLegend attribute={{check: !!startDate, name: "rental-start", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}} />
              </div>
              <div className="col-6">
                <label className="form-label">{lang.labels.rentalEnd}</label>
                <input className="form-control" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
            <div className="mb-5">
              <div className="d-flex flex-stack mb-3">
                <div className="d-flex flex-column">
                  <label className="form-label mb-0">{lang.labels.autoRenew}</label>
                </div>
                <div className="d-flex justify-content-end">
                  <div className="form-check form-check-solid form-check-custom form-switch">
                    <input className="form-check-input w-50px h-30px" type="checkbox" checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)} />
                  </div>
                </div>
              </div>
              { autoRenew &&
                <div>
                  <label className="form-label">{lang.labels.renewDaysBefore}</label>
                  <input className="form-control" type="number" min="0" value={renewDaysBefore} onChange={(e) => setRenewDaysBefore(e.target.value)} />
                </div>
              }
            </div>
            <div className="separator separator-dashed my-5"></div>
            <div className="mb-3">
              <label className="form-label">{lang.labels.rentalDepositAmount}</label>
              <input
                className="form-control"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                style={{fontVariantNumeric: 'tabular-nums'}}
              />
              <div className="form-text text-muted fs-8">{lang.labels.defaultDepositAmount}</div>
              <AppLegend attribute={{check: !depositAmount || (!isNaN(parseFloat(depositAmount)) && parseFloat(depositAmount) >= 0), name: "rental-deposit", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}} />
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-light" type="button" data-bs-dismiss="modal" onClick={resetForm}>{lang.labels.cancel}</button>
            <button className="btn btn-primary" type="button" disabled={loading || !hasOptions} onClick={submitRental}>
              {loading ? lang.labels.pleaseWait : lang.labels.confirmRental}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalAssetRent;
